import { computed, inject, onMounted, ref } from "vue"
import { storeToRefs } from "pinia"
import type { PurchaseItem } from "@/models/purchase-item.model"
import type { Moment } from "moment"
import { PurchaseSurchargePeriodController } from "@/controllers/model-controllers/purchase-surcharge-period-controller"
import { usePurchaseSurchargePeriodStore } from "@/store/purchase-surcharge-periods.store"
import { normalizeDecimal } from "@/helper/number-helper"

export default function usePurchaseSurchargePeriodRetrieval(
  purchaseItem: PurchaseItem,
  date: Moment
) {
  const controller: PurchaseSurchargePeriodController = inject("purchaseSurchargePeriodController")!
  const { purchaseSurchargePeriods } = storeToRefs(usePurchaseSurchargePeriodStore())
  const isLoading = ref(false)

  // Derive the period for this item/date reactively from the socket-synced store, so backend
  // changes (create/update/delete) made via the Django admin popup update the displayed value live.
  const surchargePeriod = computed(() => {
    return (
      purchaseSurchargePeriods.value.find(
        (period) =>
          period.purchaseItem === purchaseItem.id &&
          period.periodStart.isSameOrBefore(date) &&
          period.periodEnd.isSameOrAfter(date)
      ) ?? null
    )
  })

  const surchargeDetail = computed(() => {
    if (!surchargePeriod.value) {
      return null
    }

    const parts: string[] = []
    if (surchargePeriod.value.surchargePercent) {
      parts.push(`${normalizeDecimal(surchargePeriod.value.surchargePercent)}% over bedrag`)
    }
    if (surchargePeriod.value.surchargePerUnit) {
      parts.push(`€ ${normalizeDecimal(surchargePeriod.value.surchargePerUnit)} per eenheid`)
    }
    if (surchargePeriod.value.surchargeFlat) {
      parts.push(`€ ${normalizeDecimal(surchargePeriod.value.surchargeFlat)} per verstrekking`)
    }

    return parts.join(" en ")
  })

  const surchargeUrl = computed(() => {
    if (surchargePeriod.value) {
      return `${import.meta.env.VITE_API_URL}/admin/administration/purchasesurchargeperiod/${surchargePeriod.value.id}/change/?_autoclose=1`
    }

    const periodStart = date.clone().startOf("isoWeek").format("YYYY-MM-DD")
    const periodEnd = date.clone().endOf("isoWeek").format("YYYY-MM-DD")
    const params = new URLSearchParams({
      purchase_item: purchaseItem.id,
      period_start: periodStart,
      period_end: periodEnd,
      _autoclose: "1"
    })
    return `${import.meta.env.VITE_API_URL}/admin/administration/purchasesurchargeperiod/add/?${params.toString()}`
  })

  function openSurchargeForm() {
    const width = 600
    const height = 1000
    const y = window.top!.outerHeight / 2 + window.top!.screenY - height / 2
    const x = window.top!.outerWidth / 2 + window.top!.screenX - width / 2
    window.open(surchargeUrl.value, "_blank", `width=${width},height=${height},left=${x},top=${y}`)
  }

  onMounted(async () => {
    isLoading.value = true
    // Initial fetch lands in the store; subsequent changes arrive via socket synchronization.
    await controller.findByPurchaseItemAndDate(purchaseItem.id, date.format("YYYY-MM-DD"))
    isLoading.value = false
  })

  return {
    surchargePeriod,
    surchargeDetail,
    isLoading,
    openSurchargeForm
  }
}
